import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '../../../componentsv2/Typography';
import Button from '../../../componentsv2/Button';
import { CrossIcon } from '../../../components/Icons';
import StepButtons from './StepButtons';
import { useTranslation } from '../../../hooks/i18n';
import { COLORS } from '../../../themev2';
import { IAsset } from '../../../types/documents';
import { CountryKeys } from '../../../types';
import { DocumentSlotKey, getDocumentSlotLabels, getRequiredDocumentSlots } from '../../../constants/data';

export type DocumentSlotFiles = Partial<Record<DocumentSlotKey, File[]>>;
export type DocumentSlotAssets = Partial<Record<DocumentSlotKey, IAsset[]>>;

interface DocumentDropzoneProps {
  slotKey: DocumentSlotKey;
  label: string;
  file: File | undefined;
  existingAssets: IAsset[];
  isUploading?: boolean;
  hasError: boolean;
  onDrop: (slotKey: DocumentSlotKey, files: File[]) => void;
  onRemoveFile: (slotKey: DocumentSlotKey) => void;
  onDeleteAsset?: (assetId: number) => void;
}

const DocumentDropzone = ({
  slotKey,
  label,
  file,
  existingAssets,
  isUploading,
  hasError,
  onDrop,
  onRemoveFile,
  onDeleteAsset,
}: DocumentDropzoneProps) => {
  const { t } = useTranslation(['activation', 'common']);
  const hasExistingFile = existingAssets.length > 0;
  const isDisabled = hasExistingFile || !!file;

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (isDisabled) return;
      onDrop(slotKey, acceptedFiles.slice(0, 1));
    },
    accept:
      'application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, image/png, image/jpeg',
    maxFiles: 1,
    disabled: isDisabled,
  });

  return (
    <Grid item xs={12}>
      <Typography sx={{ fontWeight: 600, fontSize: '14px', color: COLORS.gray[1200], mb: 0.5 }}>
        {label}
      </Typography>
      <Box
        {...getRootProps()}
        sx={{
          padding: { xs: '14px 14px', sm: '16px 18px' },
          borderRadius: '8px',
          border: `1px ${hasError ? 'solid #930909' : 'dashed #91A2BB'}`,
          backgroundColor: hasError ? '#F8E1E133' : isDisabled ? '#F5F5F5' : '#F1F6FD',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1, sm: 1.5 },
          cursor: isDisabled ? 'default' : 'pointer',
          outline: 'none',
          opacity: isDisabled ? 0.5 : 1,
          transition: 'border .24s ease-in-out, opacity .24s ease-in-out',
          ...(!isDisabled && {
            '&:hover': {
              border: hasError ? undefined : '1px dashed #3B4567',
              backgroundColor: hasError ? undefined : '#E8F0FC',
            },
          }),
        }}
      >
        <input {...getInputProps()} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1.25,
            flex: 1,
            minWidth: 0,
          }}
        >
          <svg width="32" height="32" viewBox="0 0 48 48" fill="none" style={{ flexShrink: 0 }}>
            <rect width="48" height="48" rx="24" fill="#E4EEFC" />
            <path
              d="M20 20L28 20M20 24L28 24M20 28L24 28M16 34H32C33.1046 34 34 33.1046 34 32V16C34 14.8954 33.1046 14 32 14H16C14.8954 14 14 14.8954 14 16V32C14 33.1046 14.8954 34 16 34Z"
              stroke="#91A2BB"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: '14px', lineHeight: 1.35, color: COLORS.gray[1100] }}>
              {t('activation:step3.fileUploadPrompt')}
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                lineHeight: 1.35,
                color: COLORS.gray[1000],
                mt: 0.25,
              }}
            >
              {t('common:fileUploaderBox.availableFiles')}
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" component="span" disabled={isDisabled} size="small" isFullHeight={false}>
          {t('common:fileUploaderBox.button')}
        </Button>
      </Box>
      {hasError && (
        <Box
          sx={{
            color: '#C54646',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            paddingTop: '0.5em',
          }}
        >
          <CrossIcon size="sm" />
          <Typography sx={{ fontSize: '12px', lineHeight: '18px', color: '#C54646' }}>
            {t('common:fileUploaderBox.error')}
          </Typography>
        </Box>
      )}
      {file && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', mt: 1 }}>
          <Box
            sx={{
              background: '#E6F6FB',
              padding: '4px 10px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontWeight: 500, fontSize: '14px', lineHeight: '21px', color: '#3B4567' }}>
              {file.name}
            </Typography>
            <IconButton sx={{ color: '#0B607D' }} onClick={() => onRemoveFile(slotKey)}>
              <CrossIcon size="sm" />
            </IconButton>
          </Box>
        </Box>
      )}
      {hasExistingFile && (
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '14px', color: COLORS.gray[1200] }}>
            {t('activation:step3.uploadedFiles', 'Uploaded files')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', mt: 0.5 }}>
            {existingAssets.map((asset) => (
              <Box
                key={asset.id}
                sx={{
                  background: '#D4EDDA',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ fontWeight: 500, fontSize: '14px', lineHeight: '21px', color: '#155724' }}>
                  {asset.fileName}
                </Typography>
                {onDeleteAsset && (
                  <IconButton
                    sx={{ color: '#155724', marginLeft: '4px' }}
                    disabled={isUploading}
                    onClick={() => onDeleteAsset(asset.id)}
                  >
                    <CrossIcon size="sm" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Grid>
  );
};

interface DocumentsProps {
  country: CountryKeys;
  entityType: string;
  onNext: (files: DocumentSlotFiles) => void;
  onBack: () => void;
  isUploading?: boolean;
  existingAssets?: DocumentSlotAssets;
  onDeleteAsset?: (slotKey: DocumentSlotKey, assetId: number) => void;
}

const Documents = ({
  country,
  entityType,
  onNext,
  onBack,
  isUploading,
  existingAssets = {},
  onDeleteAsset,
}: DocumentsProps) => {
  const { t } = useTranslation(['activation', 'common']);
  const requiredSlots = getRequiredDocumentSlots(country, entityType);
  const [files, setFiles] = useState<DocumentSlotFiles>({});
  const [errors, setErrors] = useState<Partial<Record<DocumentSlotKey, boolean>>>({});

  const slotLabels = getDocumentSlotLabels(t, country);

  const handleDrop = (slotKey: DocumentSlotKey, acceptedFiles: File[]) => {
    setFiles((prev) => ({ ...prev, [slotKey]: acceptedFiles }));
    if (errors[slotKey]) {
      setErrors((prev) => ({ ...prev, [slotKey]: false }));
    }
  };

  const handleRemoveFile = (slotKey: DocumentSlotKey) => {
    setFiles((prev) => {
      const next = { ...prev };
      delete next[slotKey];
      return next;
    });
  };

  const handleNext = () => {
    const newErrors: Partial<Record<DocumentSlotKey, boolean>> = {};
    let hasAnyError = false;
    for (const slotKey of requiredSlots) {
      const hasFile = !!files[slotKey]?.length;
      const hasExisting = !!existingAssets[slotKey]?.length;
      if (!hasFile && !hasExisting) {
        newErrors[slotKey] = true;
        hasAnyError = true;
      }
    }
    if (hasAnyError) {
      setErrors(newErrors);
      return;
    }
    onNext(files);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Grid container rowSpacing={2}>
        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography
            sx={{ fontWeight: 600, fontSize: '24px', lineHeight: '36px', color: COLORS.brand.night }}
          >
            {t('activation:step3.title')}
          </Typography>
          <Typography
            sx={{ fontSize: '18px', fontWeight: 400, lineHeight: '27px', color: COLORS.gray[1100] }}
          >
            {t('activation:step3.subtitle')}
          </Typography>
        </Grid>
        {requiredSlots.map((slotKey) => (
          <DocumentDropzone
            key={slotKey}
            slotKey={slotKey}
            label={slotLabels[slotKey]}
            file={files[slotKey]?.[0]}
            existingAssets={existingAssets[slotKey] ?? []}
            isUploading={isUploading}
            hasError={!!errors[slotKey]}
            onDrop={handleDrop}
            onRemoveFile={handleRemoveFile}
            onDeleteAsset={onDeleteAsset ? (assetId) => onDeleteAsset(slotKey, assetId) : undefined}
          />
        ))}
      </Grid>
      <StepButtons onNext={handleNext} onBack={onBack} isNextLoading={isUploading} />
    </Box>
  );
};

export default Documents;
